import React from 'react';
import {
  ASIN_METRIC_TYPE,
  ASIN_TYPE,
  ASIN_TYPE_NAMES,
  COMPONENT_NAMES,
  METRIC_NAMES,
  STABILIZATION,
} from '../../../constants/asin_metric_elements';
import { COMPONENT_TO_LINK } from '../../../constants/urls';
import { capitalize } from '../../../utilities';
import { dateToUnix, getEndOfMonthDate, YYYYMMDDToUnix, YYYYMMToDate } from '../../../utils/date-utils';
import ComponentLink from '../../content/controls/component_link';

const KEY_SEPARATOR = '-';

export const generateCompositeKey = (month, metric, component, asinType = 'ALL') => {
  const keys = [month, metric, component, asinType];
  return keys.join(KEY_SEPARATOR);
};

export const generateLabel = (compositeKey, displayMonth = false,
                              displayMetric = false, displayAsinType = false) => {
  const [month, metric, component, asinType] = compositeKey.split(KEY_SEPARATOR);
  let labelKeys = [];
  labelKeys.push(COMPONENT_NAMES[component]);
  if(displayMonth) labelKeys.push(month);
  if(displayMetric) labelKeys.push(METRIC_NAMES[metric]);
  if(displayAsinType) labelKeys.push(ASIN_TYPE_NAMES[asinType]);
  return labelKeys.join(KEY_SEPARATOR);
};

export const generateStabilizationLabel = (compositeKey, displayMonth = false,
                                           displayMetric = false, displayAsinType = false) => {
  const label = generateLabel(compositeKey, displayMonth, displayMetric, displayAsinType);
  return [label, STABILIZATION].join(KEY_SEPARATOR);
}

export const extractComponent = (compositeKey) => {
  const [, , component, ] = compositeKey.split(KEY_SEPARATOR);
  return component;
};

export const extractMetric = (compositeKey) => {
  const [, metric, , ] = compositeKey.split(KEY_SEPARATOR);
  return metric;
};

export const extractMonth = (compositeKey) => {
  const [month, , , ] = compositeKey.split(KEY_SEPARATOR);
  return month;
};

export const extractAsinType = (compositeKey) => {
  const [, , , asinType] = compositeKey.split(KEY_SEPARATOR);
  return asinType;
}

export const getComponentLink = (name) => {
  return (<ComponentLink componentName={COMPONENT_NAMES[name] || name} mapping={COMPONENT_TO_LINK} level={0} />);
};

export const formatValue = (value, displayInMillions) => {
  if(isNaN(value)){
    return 'N/A';
  } else if (displayInMillions) {
    return new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(value / 1000000);
  } else {
    return new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(value);
  }
};

export const filterLinesOrBars = (lines, selectedComponents, selectedMetrics, selectedMonths, selectedAsinTypes) => {
  let filteredLines = lines;
  if (selectedComponents) filteredLines = lines.filter(id => selectedComponents.some(comp => comp === extractComponent(id)));
  if (selectedMetrics) filteredLines = filteredLines.filter(id => selectedMetrics.some(metric => metric === extractMetric(id)));
  if (selectedMonths) filteredLines = filteredLines.filter(id => selectedMonths.some(month => month === extractMonth(id)));
  if (selectedAsinTypes) filteredLines = filteredLines.filter(id => selectedAsinTypes.some(asinType => asinType === extractAsinType(id)));
  return filteredLines
};

export const getColumnDefinitions = (items, displayInMillions, isAsinMetrics = false) => {
  let columnDefinition = [];
  const item = items && items.length > 0 ? items[0] : {};
  for(const key of Object.keys(item)){
    if(key === 'component'){
      columnDefinition.push({id: key, header: 'Component', cell: (item) => getComponentLink(item[key])});
    } else if (key === 'snapshot') {
      columnDefinition.push({id: key, header: 'As of Date', cell: (item) => item[key]});
    } else if([ASIN_METRIC_TYPE.NET_VARIANCE, ASIN_METRIC_TYPE.ABS_VARIANCE,
               ASIN_METRIC_TYPE.FA_UNITS, ASIN_METRIC_TYPE.LA_UNITS].includes(key)) {
      columnDefinition.push({id: key, header: METRIC_NAMES[key], cell: (item) => formatValue(item[key], displayInMillions)});
    } else if([ASIN_METRIC_TYPE.NET_FA, ASIN_METRIC_TYPE.NET_LA].includes(key)){
      if(isAsinMetrics){
        columnDefinition.push({id: key, header: METRIC_NAMES[key], cell: (item) => item[key].toFixed(2)});
      } else {
        columnDefinition.push({id: key, header: METRIC_NAMES[key], cell: (item) => formatValue(item[key], displayInMillions)});
      }
    } else if (key === 'asin'){
      columnDefinition.push({id: key, header: 'ASIN', cell: (item) => (
        <a href={"https://www.amazon.com/s?k=" + item[key]} target="_blank"> {item[key]} </a>
        )});
    } else if(METRIC_NAMES.hasOwnProperty(key)) {
      columnDefinition.push({id: key, header: METRIC_NAMES[key],
                              cell: (item) => !isNaN(item[key]) ? item[key].toFixed(2) : 'N/A'});
    } else {
      columnDefinition.push({id: key, header: capitalize(key),
                              cell: (item) => item[key]});
    }
  }
  return columnDefinition;
};

export const mergeKeysOfObjectsWithSameId = (data, id) => {
  return Object.values(data.reduce((c, v) => {
    c[v[id]] = {...c[v[id]], ...v};
    return c;
  }, {}));
};

export const getComponentMetricMap = (items) => {
  // asinType => snapshot => Map(component => Map(metric=> metric value))
  const latestSnapshot = items.map(item => parseInt(item.snapshot))
                              .reduce((currentLatestSnapshot, snapshot) =>
                                        Math.max(currentLatestSnapshot, snapshot), 0);
  let metricsMap = new Map();
  for(const item of items){
    const asinType = item.asinType || ASIN_TYPE.ALL;
    if(!metricsMap.has(asinType)){
      metricsMap.set(asinType, new Map());
    }
    let snapshotToComponentMetricsMap = metricsMap.get(asinType);

    const snapshot = item.snapshot;
    if(!snapshotToComponentMetricsMap.has(snapshot)){
      snapshotToComponentMetricsMap.set(snapshot, new Map());
    }
    let componentToMetricsMap = snapshotToComponentMetricsMap.get(snapshot);
    const componentValues = item.componentValues;
    const metric = item.metric;
    const units = item.units;
    if(componentValues){
      for (const component of Object.keys(componentValues)){
        if(!componentToMetricsMap.has(component)){
          componentToMetricsMap.set(component, new Map());
        }
        let metricsMap = componentToMetricsMap.get(component);
        if(!metricsMap.has(metric)){
          metricsMap.set(metric, componentValues[component]);
        }
        /** Although FA_UNITS and LA_UNITS are not linked to components (they are linked to gls)
         * We are mapping them to components here so that in GL Metric tables,
         * we have FA_UNITS and LA_UNITS columns for each component.
         */
        if(metric === ASIN_METRIC_TYPE.NET_FA){
          metricsMap.set(ASIN_METRIC_TYPE.FA_UNITS, units);
        } else if (metric === ASIN_METRIC_TYPE.NET_LA) {
          metricsMap.set(ASIN_METRIC_TYPE.LA_UNITS, units);
        }
      }
    }
  }
  return [metricsMap, latestSnapshot.toString()];
}

export const getTrendGraphSnapshotMap = (metricItems) => {
  let snapshotMap = new Map();
  for(const metricItem of metricItems){
    const {snapshot, metric, month, componentValues, asinType} = metricItem;
    const trendData = Object.keys(componentValues).reduce( (acc, comp) => {
      acc[generateCompositeKey(month, metric, comp, asinType)] = componentValues[comp];
      return acc;
    }, {});
    if(!snapshotMap.has(snapshot)) snapshotMap.set(snapshot, {snapshot: YYYYMMDDToUnix(snapshot)});
    let snapshotRow = snapshotMap.get(snapshot);
    Object.assign(snapshotRow, trendData);
  }
  return snapshotMap;
}

/* Convert a list of glMetricItems or asinMetricItems to component rows
 Sample Input:
 [{
 channel: "Retail"
 componentValues: {associateFees: 196.03714162069707, badDebt: 200, closingFees: 200, ...}
 gl: 201
 marketplaceId: 1
 metric: "SMAPE_ACCURACY"
 month: 202009
 snapshotDate: 20210107}, ...]

 Sample Output:
 [{
 component: associateFees,
 NET_FA: 80,
 NET_LA: 90,
 SMAPE_ACCURACY: 196.03714162069707}, ...]
 */
export const generateGlOrAsinMetricsReportRows = (metricItems) => {
  const [metricsMap, latestSnapshot] = getComponentMetricMap(metricItems);
  //Display ALL asinType in metrics table
  const snapshotToComponentMetricMap = metricsMap.has(ASIN_TYPE.ALL) ?
                                       metricsMap.get(ASIN_TYPE.ALL) : new Map();
  const componentToMetricsMap = snapshotToComponentMetricMap.has(latestSnapshot) ?
                                snapshotToComponentMetricMap.get(latestSnapshot) : new Map();
  const reportRows = [];
  for (const [key, value] of componentToMetricsMap.entries()){
    reportRows.push({component: key, [ASIN_METRIC_TYPE.NET_FA]: value.get(ASIN_METRIC_TYPE.NET_FA),
                      [ASIN_METRIC_TYPE.NET_LA]: value.get(ASIN_METRIC_TYPE.NET_LA),
                      [ASIN_METRIC_TYPE.FA_UNITS]: value.get(ASIN_METRIC_TYPE.FA_UNITS),
                      [ASIN_METRIC_TYPE.LA_UNITS]: value.get(ASIN_METRIC_TYPE.LA_UNITS),
                      [ASIN_METRIC_TYPE.NET_VAR_PER_UNIT]: value.get(ASIN_METRIC_TYPE.NET_VAR_PER_UNIT),
                      [ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT]: value.get(ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT),
                      [ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT]: value.get(ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT),
                      [ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT]: value.get(ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT)
                    });
  }
  return [reportRows, metricsMap, latestSnapshot];
}

export const getStabilizationUnixTimestamp = (yearMonth, stabilizationPeriodInDays) => {
  const endOfMonthDate = getEndOfMonthDate(yearMonth);
  let stabilizationDate = YYYYMMToDate(yearMonth);
  stabilizationDate.setDate(endOfMonthDate.getDate() + stabilizationPeriodInDays);
  return dateToUnix(stabilizationDate);
}


// WEBPACK FOOTER //
// ./src/components/reports/asin_metrics/asin_metrics_utils.jsx